import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../CartContext";
import { useAuth } from "../AuthContext";
import { api, fmtINR } from "../api";
import { Beaker, ShoppingBag, X, Sparkles, Heart, Gift } from "lucide-react";
import KitDetailModal from "./KitDetailModal";

export default function ProductCard({ product, initiallySaved = false, onWishlistChange }) {
    const { add } = useCart();
    const { user } = useAuth();
    const [showIng, setShowIng] = useState(false);
    const [pairing, setPairing] = useState(null); // {kit} when modal shown
    const [showKitDetail, setShowKitDetail] = useState(false);
    const [saved, setSaved] = useState(initiallySaved);
    const [savingWl, setSavingWl] = useState(false);

    useEffect(() => { setSaved(initiallySaved); }, [initiallySaved]);

    const handleAdd = async (e) => {
        e?.preventDefault?.(); e?.stopPropagation?.();
        if (product.category === "Kits") {
            setShowKitDetail(true);
            return;
        }
        if (product.requires_kit) {
            try {
                const { data } = await api.get(`/products/${product.id}/pairing`);
                if (data.kit) {
                    setPairing(data.kit);
                    return;
                }
            } catch (err) {
                console.warn("Kit pairing lookup failed, adding directly:", err?.message || err);
            }
        }
        add(product, 1);
    };

    const toggleWishlist = async (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!user) { window.location.href = "/login"; return; }
        if (savingWl) return;
        setSavingWl(true);
        try {
            const { data } = await api.post("/wishlist", { product_id: product.id });
            setSaved(data.action === "added");
            onWishlistChange?.();
        } catch (err) {
            console.warn("Wishlist toggle failed:", err?.message || err);
        } finally { setSavingWl(false); }
    };

    return (
        <>
            <div className="flex flex-col h-full group relative hover-float rounded-xl" data-testid={`product-card-${product.id}`}>
                <button
                    onClick={toggleWishlist}
                    aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
                    data-testid={`wishlist-toggle-${product.id}`}
                    className={`absolute top-2 right-2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/80 backdrop-blur-sm border border-[var(--line-soft)] transition
                        ${saved ? "text-[var(--terra)]" : "text-[var(--ink-soft)] hover:text-[var(--terra)]"}`}
                >
                    <Heart size={15} strokeWidth={1.8} fill={saved ? "currentColor" : "none"} />
                </button>
                <Link to={`/product/${product.id}`} className="block mb-5">
                    <div className="prod-img mb-5">
                        {product.images && product.images.length > 0 ? (
                            <img src={product.images[0]} alt={product.name} />
                        ) : (
                            <span className="text-[64px]">{product.emoji || "🌿"}</span>
                        )}
                    </div>
                </Link>
                <Link to={`/product/${product.id}`} className="prod-title text-[15px] leading-snug mb-3 hover:text-[var(--taupe)] transition">
                    {product.name}
                </Link>
                <p className="text-[13px] text-[var(--body)] leading-relaxed mb-4 min-h-[40px]">
                    {product.short_desc || product.description}
                </p>
                <div className="text-[13px] text-[var(--ink-soft)] mb-4">
                    ₹ {Math.round(product.price)} · {product.size || (product.category === "Kits" ? "Set" : "Single")}
                </div>
                <div className="flex flex-col gap-2 mt-auto">
                    <button
                        onClick={() => setShowIng(true)}
                        className="btn btn-outline btn-sm w-full"
                        data-testid={`explore-formulation-${product.id}`}
                    >
                        <Beaker size={13} strokeWidth={1.6} /> Explore Formulation
                    </button>
                    <button
                        onClick={handleAdd}
                        className="btn btn-primary btn-sm w-full"
                        data-testid={`add-to-cart-${product.id}`}
                    >
                        {product.requires_kit ? <><Gift size={13} strokeWidth={1.6} /> Better as a Kit</> : <><ShoppingBag size={13} strokeWidth={1.6} /> Add to Cart</>}
                    </button>
                </div>
            </div>

            {showIng && (
                <FormulationModal product={product} onClose={() => setShowIng(false)} />
            )}
            {pairing && (
                <PairingModal
                    product={product}
                    kit={pairing}
                    onClose={() => setPairing(null)}
                    onAddKit={() => { add(pairing, 1); setPairing(null); }}
                    onForceAdd={() => { add(product, 1); setPairing(null); }}
                />
            )}
            {showKitDetail && (
                <KitDetailModal kit={product} onClose={() => setShowKitDetail(false)} />
            )}
        </>
    );
}

function PairingModal({ product, kit, onClose, onAddKit, onForceAdd }) {
    return (
        <div className="drawer-backdrop" onClick={onClose} data-testid="pairing-modal-backdrop">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white max-w-[480px] mx-auto my-12 rounded-2xl overflow-hidden shadow-xl"
                data-testid={`pairing-modal-${product.id}`}
            >
                <div className="p-6 sm:p-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-alt)] grid place-items-center mx-auto mb-4">
                        <Gift size={20} className="text-[var(--taupe)]" />
                    </div>
                    <h3 className="font-serif text-2xl text-[var(--ink)] mb-2">Better as a complete ritual</h3>
                    <p className="text-[14px] text-[var(--body)] mb-5">
                        <b>{product.name}</b> works most effectively as part of <b>{kit.name}</b>. The kit includes complementary formulations designed to compound their benefits.
                    </p>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--bg-alt)] mb-5">
                        <div className="w-14 h-14 rounded-lg bg-white grid place-items-center overflow-hidden shrink-0">
                            {kit.images?.[0] ? <img src={kit.images[0]} alt={kit.name} className="w-full h-full object-cover" /> : <span className="text-3xl">{kit.emoji || "🎁"}</span>}
                        </div>
                        <div className="text-left flex-1">
                            <div className="text-[13px] font-medium text-[var(--ink)]">{kit.name}</div>
                            <div className="text-[12px] text-[var(--body)]">{fmtINR(kit.price)}</div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button onClick={onAddKit} className="btn btn-primary w-full" data-testid={`pairing-add-kit-${product.id}`}>
                            Add the Kit ({fmtINR(kit.price)})
                        </button>
                        <button onClick={onForceAdd} className="btn btn-outline w-full" data-testid={`pairing-add-anyway-${product.id}`}>
                            Add {product.name} alone
                        </button>
                        <button onClick={onClose} className="text-[12px] text-[var(--body)] mt-1 hover:text-[var(--ink)]">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FormulationModal({ product, onClose }) {
    const { add } = useCart();
    const ingredients = product.ingredients || [];
    return (
        <div className="drawer-backdrop" onClick={onClose} data-testid="formulation-modal-backdrop">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white max-w-[680px] mx-auto my-12 rounded-2xl overflow-hidden shadow-xl"
                data-testid={`formulation-modal-${product.id}`}
            >
                <div className="grid md:grid-cols-2">
                    <div className="prod-img !rounded-none aspect-square">
                        {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} />
                        ) : (
                            <span className="text-[80px]">{product.emoji || "🌿"}</span>
                        )}
                    </div>
                    <div className="p-6 lg:p-8 flex flex-col">
                        <button onClick={onClose} className="self-end text-[var(--body)] hover:text-[var(--ink)] -mt-2 -mr-2 p-1" data-testid="close-formulation-modal">
                            <X size={18} />
                        </button>
                        <div className="overline mb-1.5">{product.category}</div>
                        <h3 className="prod-title text-[18px] leading-snug mb-2">{product.name}</h3>
                        <div className="text-[12.5px] text-[var(--body)] mb-3">{product.size || "Single"} · {fmtINR(product.price)}</div>
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] mb-3">
                            <Sparkles size={12} /> Key Ingredients
                        </div>
                        <div className="flex-1 overflow-y-auto pr-1 -mr-1 max-h-[300px] mb-4">
                            {ingredients.length === 0 ? (
                                <p className="text-[13px] text-[var(--body)]">Ingredient list coming soon.</p>
                            ) : (
                                <ul className="space-y-3">
                                    {ingredients.map((ing, i) => (
                                        <li key={ing.name || `ing-${i}`} className="border-b border-[var(--line-soft)] pb-2.5">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="font-serif text-[15px] text-[var(--ink)]">{ing.name}</span>
                                                {ing.percentage && (
                                                    <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--taupe)] shrink-0">{ing.percentage}</span>
                                                )}
                                            </div>
                                            {ing.benefit && (
                                                <p className="text-[12px] text-[var(--body)] leading-relaxed mt-1">{ing.benefit}</p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex gap-2 mt-auto">
                            <Link to={`/product/${product.id}`} className="btn btn-outline btn-sm flex-1" onClick={onClose}>Full details</Link>
                            <button onClick={() => { add(product, 1); onClose(); }} className="btn btn-primary btn-sm flex-1" data-testid={`modal-add-to-cart-${product.id}`}>
                                <ShoppingBag size={13} /> Add to Cart
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
